import { RequirePermission } from "@components/common/require-permission";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useState } from "react";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { BaseProjectProps } from "@custom-types/base-types";
import { NotificationDialog, Switch, useToast, Text } from "@flat-ui";

/** A switch to toggle the project standalone */
export function ProjectStandaloneSwitch({
  project,
}: BaseProjectProps): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();

  const [isProjectStandalone, setIsProjectStandalone] = useState<boolean>(
    project.standalone
  );
  const [shouldShowConfirmation, setShouldShowConfirmation] =
    useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const { handleErrorWithDialog } = useErrorHandler();

  /** Toggle project's standalone after user clicks on the Switch button */
  async function handleOnDialogConfirm(): Promise<void> {
    trackEvent({
      name: ProjectEvents.makeProjectStandalone,
      props: { shouldStandalone: !isProjectStandalone },
    });

    setIsFetching(true);
    try {
      const { standalone: isStandalone } =
        await faroCoreApiClient.admin.changeStandaloneStateOfProject({
          projectId: project.id,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          payload: { standalone: !isProjectStandalone },
        });

      setIsProjectStandalone(isStandalone);
      openToast({
        title: `Project is set to ${
          isStandalone ? "standalone" : "non-standalone"
        } successfully`,
      });
    } catch (error) {
      handleErrorWithDialog({
        id: `project-standalone-switch-${Date.now().toString()}`,
        title: "The project's standalone state could not be changed",
        error,
      });
    } finally {
      setShouldShowConfirmation(false);
      setIsFetching(false);
    }
  }

  return (
    <>
      <RequirePermission permission="project.changeStandalone">
        <Switch
          checked={isProjectStandalone}
          onToggle={() => setShouldShowConfirmation(true)}
          label="Standalone"
        />
      </RequirePermission>

      <NotificationDialog
        notificationType="warning"
        open={shouldShowConfirmation}
        title={`You are about to set the project to ${
          isProjectStandalone ? "non-standalone" : "standalone"
        }`}
        onCancel={() => setShouldShowConfirmation(false)}
        onConfirm={handleOnDialogConfirm}
        showSpinner={isFetching}
      >
        <Text variant="bodyL">
          This will have an effect on the subscription and available
          functionality of the project. It could influence the accessibility of
          the project data for the workspace. Please only confirm if you fully
          understand the implications.
        </Text>
      </NotificationDialog>
    </>
  );
}
